import { Flex } from "grid-styled";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import system from "system-components";

import { RegulationItem } from "pstat-anywhere/components/document_control/view_policy/standards_and_regulations/StandardsAndRegulationsPanel";
import Properties, {
  AuthorSearchLink,
  DateKeyValueRow,
  KeyValueRow
} from "pstat-anywhere/components/view/Properties";
import { withLabelContext } from "pstat-anywhere/context_providers/LabelContext";
import { withTenantContext } from "pstat-anywhere/context_providers/TenantContext";
import { getRootUrl } from "pstat-anywhere/utils/urls";
import RouterFocusLink from "pstat-design-system/RouterFocusLink";
import { Text } from "pstat-design-system/text/paragraph";
import Tooltip from "pstat-design-system/Tooltip";

export const PreviewValueSpan = system({
  is: Text,
  display: "inline-block",
  p: 1
});

export const CategorySearchLink = props => {
  const category = props.category;
  const pathname = `${getRootUrl()}/search`;
  const query = {
    category: category.pk
  };
  return (
    <RouterFocusLink
      to={{
        pathname: pathname,
        query: query
      }}
    >
      {category.name}
    </RouterFocusLink>
  );
};

const ApplicabilitySectionLink = props => {
  const applicability = props.applicability;
  const pathname = window.location.pathname;
  const hash = "#applicability-panel";
  return (
    <RouterFocusLink
      to={{
        pathname: pathname,
        hash: hash
      }}
      data-tip={true}
      data-for="applicability-tenants-tooltip"
    >
      {applicability.name}
    </RouterFocusLink>
  );
};

const MoreItemsMoveToPanelLink = ({ moreCounter, panelHash }) => {
  const { t } = useTranslation();
  return (
    <RouterFocusLink
      pb={1}
      to={{
        pathname: window.location.pathname,
        hash: panelHash
      }}
    >
      {t("nav.primary.manage.more.counter", { count: moreCounter })}
    </RouterFocusLink>
  );
};

const Tags = props => {
  return props.tags.map((tag, index) => {
    const pathname = `${getRootUrl()}/search`;
    const query = {
      reference: tag.pk
    };
    let tagText = tag.name;
    if (index !== props.tags.length - 1) {
      tagText += ",";
    }
    if (props.isPreview) {
      return <PreviewValueSpan>{tagText}</PreviewValueSpan>;
    } else {
      return (
        <RouterFocusLink
          key={tag.pk}
          to={{
            pathname: pathname,
            query: query
          }}
        >
          {tagText}
        </RouterFocusLink>
      );
    }
  });
};

const ListItems = ({ items, numItemsDisplay, ItemComponent, moreHash }) => {
  return items.map((item, index) => {
    if (index < numItemsDisplay) {
      return (
        <Flex>
          <ItemComponent {...item} newLine={true} />
        </Flex>
      );
    } else if (index === numItemsDisplay) {
      return (
        <Flex>
          <MoreItemsMoveToPanelLink
            moreCounter={items.length - numItemsDisplay}
            panelHash={moreHash}
          />
        </Flex>
      );
    }
  });
};

const Dates = ({
  labels,
  tenant,
  effectiveDate,
  approvalDate,
  expirationDate,
  revisionDate,
  originationDate
}) => {
  const { documentSettings } = tenant;
  return (
    <Fragment>
      {documentSettings.originationDateShow && (
        <DateKeyValueRow
          id="origination_date"
          label={labels.originationDateLabel}
          value={originationDate}
        />
      )}
      {documentSettings.approvedDateShow && (
        <DateKeyValueRow
          id="approved_date"
          label={labels.approvedDateLabel}
          value={approvalDate}
        />
      )}
      {tenant.hasScheduledEffectiveDateEnabled &&
        documentSettings.effectiveDateShow && (
          <DateKeyValueRow
            id="effective_date"
            label={labels.effectiveDateLabel}
            value={effectiveDate}
          />
        )}
      {documentSettings.revisionDateShow && (
        <DateKeyValueRow
          id="revision_date"
          label={labels.revisionDateLabel}
          value={revisionDate}
        />
      )}
      {documentSettings.expirationDateShow && (
        <DateKeyValueRow
          id="expiration_date"
          label={labels.expirationDateLabel}
          value={expirationDate}
        />
      )}
    </Fragment>
  );
};

const CenteredDiv = system({
  textAlign: "center"
});

export const ApplicabilityTenantsTootip = props => {
  const tenantList = props.bloc.tenants.map(tenant => (
    <CenteredDiv key={tenant.pk}>{tenant.name}</CenteredDiv>
  ));
  return <Tooltip name="applicability-tenants-tooltip">{tenantList}</Tooltip>;
};

const Ownership = ({
  labels,
  documentSettings,
  author,
  category,
  bloc,
  tenant,
  tags,
  isPreview,
  regulations,
  numRegulationsDisplay
}) => {
  const { t } = useTranslation();
  return (
    <Fragment>
      {documentSettings.authorShow && (
        <KeyValueRow id="author" label={labels.authorLabel}>
          {isPreview ? (
            <PreviewValueSpan>
              {author.firstName} {author.lastName}
            </PreviewValueSpan>
          ) : (
            <AuthorSearchLink author={author} />
          )}
        </KeyValueRow>
      )}
      {documentSettings.departmentShow && (
        <KeyValueRow id="category" label={labels.categoryLabel}>
          {isPreview ? (
            <PreviewValueSpan>{category.name}</PreviewValueSpan>
          ) : (
            <CategorySearchLink category={category} />
          )}
        </KeyValueRow>
      )}
      {tenant.showApplicability && (
        <KeyValueRow id="applicability" label="Applicability">
          {isPreview ? (
            <PreviewValueSpan>{bloc.name}</PreviewValueSpan>
          ) : (
            <ApplicabilitySectionLink applicability={bloc} />
          )}
        </KeyValueRow>
      )}
      {documentSettings.referencesShow && (
        <Fragment>
          {tags.length > 0 && (
            <KeyValueRow id="tags" label={labels.referencesLabel}>
              <Tags tags={tags} isPreview={isPreview} />
            </KeyValueRow>
          )}
        </Fragment>
      )}
      {regulations && (
        <Fragment>
          {regulations.length > 0 && (
            <KeyValueRow
              id="standards-and-regulations"
              label={t("nav.primary.manage.standardsAndRegs")}
            >
              <ListItems
                items={regulations}
                numItemsDisplay={numRegulationsDisplay}
                ItemComponent={RegulationItem}
                moreHash="#standards-and-regulations-panel"
              />
            </KeyValueRow>
          )}
        </Fragment>
      )}
      <ApplicabilityTenantsTootip bloc={bloc} />
    </Fragment>
  );
};

const PolicyProperties = ({
  effectiveDate,
  approvalDate,
  expirationDate,
  revisionDate,
  originationDate,
  author,
  category,
  bloc,
  tags,
  labels,
  tenant,
  headerImageUrl,
  regulations,
  isPreview = false,
  numRegulationsDisplay
}) => (
  <Properties
    headerImageUrl={headerImageUrl}
    renderDates={() => (
      <Dates
        labels={labels}
        tenant={tenant}
        effectiveDate={effectiveDate}
        approvalDate={approvalDate}
        expirationDate={expirationDate}
        revisionDate={revisionDate}
        originationDate={originationDate}
      />
    )}
    renderProperties={() => (
      <Ownership
        labels={labels}
        documentSettings={tenant.documentSettings}
        author={author}
        category={category}
        bloc={bloc}
        tenant={tenant}
        tags={tags}
        isPreview={isPreview}
        regulations={regulations}
        numRegulationsDisplay={numRegulationsDisplay}
      />
    )}
  />
);

export default withTenantContext(withLabelContext(PolicyProperties));
